import { Card, Tabs } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import {
  IconBuildingSkyscraper, IconChartPie,
  IconNote,
} from '@tabler/icons-react';

import type {
  AccidentCalculations,
  AccidentDataFormData,
} from '../../types/reports.js';
import { AccidentMatrixSection } from './accident/AccidentMatrixSection.js';
import { AdditionalDataSection } from './accident/AdditionalDataSection.js';
import { GeneralInfoSection } from './accident/GeneralInfoSection.js';
import { useTabNavigation } from './TabNavContext.js';
import { ReportTabPanel, TabErrorDot, TabWarningDot } from './ReportTabs.js';
import { hasFieldErrors, tabsWithErrors } from './tabErrors.js';
import { useFieldTabMap, useTabFieldDiscovery } from '../form/fieldTabRegistry.js';
import { ACCIDENT_TAB_ORDER, ACCIDENT_TAB_TITLES } from './reportTabTitles.js';
import { useWarnings, type WarningsStore } from './formStores.js';

const FIRST_TAB = ACCIDENT_TAB_ORDER[0];

interface AccidentDataFormProps {
  form: UseFormReturnType<AccidentDataFormData>;
  runValidation: () => void;
  /** Live-Vorschau der abgeleiteten Unfallstatistik-Kennzahlen, aus useReportCalc (D-10). */
  calc: AccidentCalculations | null;
  /**
   * Nicht-blockierende Hinweise der letzten Validierung (D-09/D-10), ausdruecklich an die
   * Unfallstatistik durchgereicht.
   *
   * Seit Plan 20-16 ist das NICHT mehr der allgemeine Weg: normale Eingabefelder holen sich
   * ihren Hinweis selbst aus `FieldWarningsContext`, in allen fuenf Formularen. Uebrig bleibt
   * genau der Sonderfall, fuer den der Prop gedacht war -- die berechneten, deaktivierten
   * Zellen der Matrix (K6) und der zeilenbezogene Sammelhinweis darunter (K9) sind keine
   * Feldkomponenten und ordnen ihre Hinweise selbst zu.
   *
   * Der Typ ist die exportierte Store-Huelle aus formStores; das interne `Store<T>`-Interface
   * wird bewusst nicht dupliziert (Praezedenzfall: `ValuesVersionStore` in FormularDForm).
   */
  warningsStore: WarningsStore;
  disabled?: boolean;
  loading?: boolean;
  /**
   * Plan 43-09 schaltet es ein, wenn der Prueffer die Aenderungsliste sehen soll: nur dann
   * muessen alle Reiter einmal mounten, damit sich jede Beschriftung anmeldet (D-13).
   */
  discoverAllFields?: boolean;
  plant?: {
    id: number;
    name: string;
    company?: {
      id: number;
      name: string;
    };
  };
}

/**
 * Praesentations-Shell (D-01): die uncontrolled form-Instanz, die Calc-Verdrahtung und die
 * Debounce/Netzwerk-Logik leben nicht mehr hier, sondern in ReportEditPage/useReportCalc.
 * Diese Komponente reicht form/calc nur noch an ihre Tabs/Sections durch.
 */
export function AccidentDataForm({
  form,
  runValidation,
  calc,
  warningsStore,
  disabled,
  loading,
  discoverAllFields,
  plant,
}: AccidentDataFormProps) {
  const { activeTab, setActiveTab } = useTabNavigation(ACCIDENT_TAB_ORDER);
  const firstTabActive = activeTab === FIRST_TAB;

  // form.errors ist State der useForm-Instanz im Elternteil: ein form.setErrors nach dem
  // Einreichen-Klick rendert bis hierher durch, die Kennzeichnung steht also auch an
  // Reitern, die gerade gar nicht gerendert sind.
  //
  // Die Karte "Feld -> Reiter" kommt seit Plan 20-16 aus der Render-Ableitung
  // (fieldTabRegistry), nicht mehr aus einer handgepflegten Liste in dieser Datei. Die
  // Entdeckungsrunde schliesst die einzige Luecke dieser Ableitung: Reiter, die in dieser
  // Sitzung noch nie gerendert wurden, haben nichts angemeldet. Sie laeuft genau einmal und
  // nur dann, wenn ueberhaupt Fehler anliegen.
  const fieldTabMap = useFieldTabMap();
  // Beide Meldungsstufen kennzeichnen ihren Reiter (Abnahmetest 21.09.2026). Hier ueber
  // `useWarnings(warningsStore)`, weil dieses Formular den Store ohnehin als Prop bekommt --
  // das Umweltformular hat ihn nicht und liest denselben Stand aus dem Kontext.
  const warnings = useWarnings(warningsStore);
  const discovering = useTabFieldDiscovery(
    ACCIDENT_TAB_ORDER,
    // Auch Hinweise loesen die Entdeckungsrunde aus: sonst bliebe der Punkt auf einem Reiter
    // aus, den der Melder nie geoeffnet hat -- gerade dort wird er gebraucht.
    (hasFieldErrors(form.errors) || hasFieldErrors(warnings) || discoverAllFields === true) &&
      !loading
  );
  const errorTabs = tabsWithErrors(form.errors, fieldTabMap);
  const warningTabs = tabsWithErrors(warnings, fieldTabMap);

  /** Rot schlaegt gelb: ein Reiter mit Fehlern zeigt den roten Punkt, auch wenn er Hinweise traegt. */
  const tabDot = (tab: string) => {
    if (errorTabs.has(tab)) return <TabErrorDot />;
    if (warningTabs.has(tab)) return <TabWarningDot />;
    return undefined;
  };

  return (
    <div onBlur={runValidation}>
      {/* keepMountedMode="display-none" gehoert zwingend zu keepMounted: der Standardmodus
          von Mantine 9 rendert nicht aktive Panels in einer versteckten React-Activity, und
          darin laufen die Anmelde-Effekte der Felder NICHT. */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        keepMounted={discovering}
        keepMountedMode="display-none"
      >
        <Tabs.List>
          <Tabs.Tab
            value="general"
            leftSection={<IconBuildingSkyscraper size={16} />}
            rightSection={tabDot('general')}
          >
            {ACCIDENT_TAB_TITLES.general}
          </Tabs.Tab>
          <Tabs.Tab
            value="statistics"
            leftSection={<IconChartPie size={16} />}
            rightSection={tabDot('statistics')}
          >
            {ACCIDENT_TAB_TITLES.statistics}
          </Tabs.Tab>
          <Tabs.Tab
            value="additional"
            leftSection={<IconNote size={16} />}
            rightSection={tabDot('additional')}
          >
            {ACCIDENT_TAB_TITLES.additional}
          </Tabs.Tab>
        </Tabs.List>

        <Card
          padding="lg"
          radius="md"
          withBorder
          style={firstTabActive ? { borderTopLeftRadius: 0 } : undefined}
        >
          <ReportTabPanel value="general">
            <GeneralInfoSection
              form={form}
              disabled={disabled}
              loading={loading}
              companyName={plant?.company?.name}
              plantName={plant?.name}
            />
          </ReportTabPanel>

          <ReportTabPanel value="statistics">
            <AccidentMatrixSection
              form={form}
              calc={calc}
              warningsStore={warningsStore}
              disabled={disabled}
              loading={loading}
            />
          </ReportTabPanel>

          <ReportTabPanel value="additional">
            <AdditionalDataSection form={form} disabled={disabled} loading={loading} />
          </ReportTabPanel>
        </Card>
      </Tabs>
    </div>
  );
}

export type { AccidentDataFormProps };
